// Approved About Us page content — Oxyvin "About Us" (26 Aug 2026).
// Content is verbatim from the approved Word document. Do not alter meaning, claims or
// factual statements without approval — only presentation (headings, grouping, layout).

export const aboutPage = {
  seo: {
    title: "About Oxyvin | Flexible Customer Engagement",
    description: "Learn how Oxyvin helps businesses build flexible customer engagement capacity through multilingual professionals, technology and adaptable operating models.",
  },
  hero: {
    heading: "Customer engagement, built around your business.",
    lede: "Oxyvin helps businesses extend their customer engagement capacity with the right people, technology and flexibility—without creating fixed capacity for every requirement. From sales and lead follow-up to customer support and multilingual engagement, we provide customer engagement professionals and technology-enabled operating models that adapt to changing business needs.",
  },
  sections: [
    {
      kind: "intro",
      heading: "Customer engagement capacity should scale up and down with your needs.",
      paragraphs: [
        "Customer engagement requirements don't always remain the same. You may need additional capacity for a campaign, a seasonal peak, a new market or a sudden increase in customer demand—and less capacity when the requirement passes.",
        "Oxyvin helps you build customer engagement capacity around your actual needs, so you can scale up when you need more and scale down when you need less.",
      ],
    },
    {
      kind: "grid",
      heading: "What is Oxyvin?",
      intro: "Oxyvin is a customer engagement services company that combines:",
      items: [
        ["People", "Multilingual customer engagement professionals who can represent your business in customer conversations."],
        ["Technology", "An enterprise-grade contact-center environment that enables structured campaigns, communication, monitoring and reporting."],
        ["Flexibility", "An operating model designed to adapt as your customer engagement requirements change."],
      ],
      closing: "Together, these allow Oxyvin to function as an extension of your customer engagement organization.",
    },
    {
      kind: "grid",
      variant: "fourR",
      heading: "Built around meaningful conversations.",
      intro: "A customer should have:",
      items: [
        ["The Right Conversation", "Relevant conversations based on the customer's requirement and stage in the journey."],
        ["The Right Time", "Follow-up when the customer is most likely to engage."],
        ["The Right Language", "Communication in a language the customer is comfortable using."],
        ["The Right Channel", "When a call doesn't work, supported digital channels such as WhatsApp can provide another way to continue the conversation."],
      ],
      closing: "These four principles guide how we think about customer engagement.",
      readMore: { label: "Read more: The Four Rs of Engagement", to: "/resources/four-rs-of-engagement" },
    },
    {
      kind: "intro",
      heading: "People are at the center.",
      paragraphs: ["Technology can make a customer engagement operation faster, more connected and more visible. But meaningful customer conversations still depend on people."],
      itemsIntro: "Oxyvin provides customer engagement professionals who can be aligned to your:",
      items: ["Campaign", "Customer journey", "Language requirements", "Processes", "Scripts", "Systems", "Quality standards"],
      closing: "The goal isn't simply to increase the number of calls. The goal is to create better customer conversations and more consistent engagement.",
    },
    {
      kind: "intro",
      heading: "A distributed workforce without geographic limitations.",
      paragraphs: ["Traditional contact centers often depend on finding the right people close to a physical location. That can become difficult when a business needs:"],
      items: ["Specific regional languages", "Additional capacity", "Specialized campaign requirements", "Temporary workforce expansion", "Coverage beyond the local talent pool"],
      closing: "Oxyvin's distributed workforce model allows customer engagement professionals to be deployed based on capability and requirement, rather than being limited entirely by geography. This is particularly valuable when language availability is a constraint.",
      readMore: { label: "Read more: Distributed Does Not Mean Disconnected", to: "/resources/distributed-does-not-mean-disconnected" },
    },
    {
      kind: "tags",
      heading: "Flexibility is part of the operating model.",
      intro: "You may need:",
      items: ["Additional capacity for a campaign", "Temporary support during a peak period", "A longer-term customer engagement team"],
      note: "Oxyvin is designed to support all three. Our objective is simple: Give businesses access to the customer engagement capacity they need, when they need it—and the flexibility to scale down when they need less.",
    },
    {
      kind: "grid",
      heading: "Where Oxyvin can help",
      intro: "Our customer engagement services include:",
      items: [
        ["Outbound Customer Engagement", "Reach customers, prospects and leads through structured outbound campaigns."],
        ["Inbound & Callback Support", "Support customer enquiries and follow up when customers request contact."],
        ["Multichannel Customer Engagement", "Combine voice with supported digital channels to create more connected customer journeys."],
        ["Overflow & Peak Support", "Add capacity when customer volumes temporarily exceed your internal team's capacity."],
        ["Flexible Customer Engagement", "Build customer engagement capacity around short-term, long-term or changing requirements."],
        ["Multilingual Customer Engagement", "Engage customers through professionals who can communicate in relevant Indian languages."],
      ],
      cta: { label: "Explore Our Services", to: "/services" },
    },
    {
      kind: "grid",
      heading: "Built for different industries.",
      intro: "Customer engagement doesn't look the same everywhere. Oxyvin supports organizations across industries including:",
      items: [
        ["Education", "Student enquiries, admissions, qualification and follow-up."],
        ["BFSI", "Customer acquisition, application follow-up, service and customer engagement."],
        ["SaaS & Technology", "Lead qualification, demo scheduling, follow-up and customer engagement."],
        ["Other Industries", "Flexible engagement programs built around specific business requirements."],
      ],
      cta: { label: "Explore Industries", to: "/industries" },
    },
    {
      kind: "steps",
      heading: "How we work",
      items: [
        ["Understand", "We start by understanding your customer journey, volumes, languages, processes and objectives."],
        ["Design", "We determine the appropriate combination of people, technology, workflow and capacity."],
        ["Deploy", "We onboard the required customer engagement professionals and establish the operating model."],
        ["Operate", "The team works according to the agreed processes, systems, quality standards and customer journey."],
        ["Measure", "Campaign activity and performance can be monitored through agreed reporting and operational visibility."],
        ["Adapt", "As requirements change, the engagement model can evolve with them."],
      ],
    },
    {
      kind: "tags",
      heading: "Technology provides the control. People provide the conversation.",
      intro: "Oxyvin uses contact-center technology to create visibility and operational control around the customer engagement workforce. Depending on the engagement, this can include:",
      items: ["Campaign management", "Agent management", "Call monitoring", "Call recording", "Performance reporting", "Customer engagement tracking", "Operational visibility", "Multichannel communication"],
      note: "Technology is not the product by itself. It is what allows people and processes to operate consistently at scale.",
      cta: { label: "Explore Our Technology", to: "/technology" },
    },
    {
      kind: "callout",
      heading: "A flexible extension of your organization.",
      text: "When you work with Oxyvin, the objective isn't to create another disconnected vendor layer. We aim to become an extension of your customer engagement organization. Your business retains control over the customer journey, processes and outcomes. Oxyvin provides the additional: People + Technology + Capacity + Operational Support required to execute the engagement.",
      emphasis: "People + Technology + Capacity + Operational Support",
    },
  ],
  vision: { heading: "Our Vision", text: "Every business deserves meaningful conversations with every customer." },
  mission: { heading: "Our Mission", text: "To make customer engagement simple, flexible, and scalable by combining multilingual professionals, an enterprise-grade customer engagement platform, and adaptable solutions that evolve with changing business needs." },
  promise: { heading: "The Oxyvin Promise", text: "The Right Conversation. At the Right Time. In the Right Language. Through the Right Channel." },
  finalCta: {
    heading: "Ready to build customer engagement around your business?",
    text: "Whether you need additional capacity for a campaign, multilingual customer engagement, support during a peak period or a longer-term customer engagement team, Oxyvin can help.",
  },
};
